import React, { useRef } from 'react';
import { Card, Field, TextField, SaveButton, useSection } from '../AdminUI';
import { Plus, Trash2, Upload } from 'lucide-react';

function fileToDataUrl(file, maxSize = 400) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = () => {
      const img = new Image();
      img.onerror = reject;
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let { width, height } = img;
        if (width > maxSize || height > maxSize) {
          const s = Math.min(maxSize / width, maxSize / height);
          width = Math.round(width * s);
          height = Math.round(height * s);
        }
        canvas.width = width; canvas.height = height;
        canvas.getContext('2d').drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL('image/png', 0.9));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}

function LogoUpload({ value, onChange }) {
  const ref = useRef(null);
  const handle = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    onChange(await fileToDataUrl(f, 200));
  };
  return (
    <div className="flex items-center gap-2">
      <div className="w-12 h-12 rounded-md bg-zinc-950 border border-zinc-800 flex items-center justify-center overflow-hidden">
        {value ? <img src={value} alt="" className="max-w-full max-h-full object-contain" /> : <span className="text-zinc-600 text-[9px]">no logo</span>}
      </div>
      <input ref={ref} type="file" accept="image/*" onChange={handle} className="hidden" />
      <button type="button" onClick={() => ref.current?.click()} className="h-8 px-2.5 rounded-md bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 text-zinc-200 text-xs flex items-center gap-1.5"><Upload size={12} /> Upload</button>
      {value && <button type="button" onClick={() => onChange('')} className="h-8 w-8 rounded-md bg-red-600/15 text-red-300 border border-red-900/40 flex items-center justify-center"><Trash2 size={12} /></button>}
    </div>
  );
}

export default function InPlaySection() {
  const [local, setLocal, save] = useSection('inPlayMatches');
  const upd = (i, k, v) => setLocal(local.map((m, idx) => idx === i ? { ...m, [k]: v } : m));
  const remove = (i) => setLocal(local.filter((_, idx) => idx !== i));
  const add = () => setLocal([...local, { id: Date.now(), name: 'New Platform', type: 'B2C White Label', tags: '', rating: '5.0', reviews: '0', logo: '', visitUrl: '', actionLink: '' }]);

  return (
    <Card title="GET ID Platforms" desc="Platforms shown in the GET ID table on homepage. Configure logo, Telegram/WhatsApp action link, and visit URL." footer={<SaveButton onClick={save} />}>
      <div className="space-y-4">
        {(local || []).map((m, i) => (
          <div key={m.id} className="rounded-xl border border-zinc-800 bg-zinc-950/80 p-4 space-y-3">
            <div className="flex items-center justify-between pb-2 border-b border-zinc-900">
              <span className="text-zinc-200 text-sm font-bold">Platform #{i + 1}: {m.name || 'Untitled'}</span>
              <button onClick={() => remove(i)} className="h-7 px-2.5 rounded-md bg-red-600/15 text-red-300 text-xs border border-red-900/40 hover:bg-red-600 hover:text-white flex items-center gap-1 transition-colors"><Trash2 size={12} /> Remove</button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
              <Field label="Platform Name"><TextField value={m.name} onChange={(v) => upd(i, 'name', v)} placeholder="e.g. Lotus 365" /></Field>
              <Field label="Category / Type"><TextField value={m.type} onChange={(v) => upd(i, 'type', v)} placeholder="e.g. B2C White Label" /></Field>
              <Field label="Tags / Features"><TextField value={m.tags || ''} onChange={(v) => upd(i, 'tags', v)} placeholder="Cricket, Casino, Live Odds" /></Field>
              <Field label="Rating (e.g. 5.0)"><TextField value={m.rating} onChange={(v) => upd(i, 'rating', v)} /></Field>
              <Field label="Reviews count"><TextField value={m.reviews} onChange={(v) => upd(i, 'reviews', v)} placeholder="1.2k" /></Field>
              <Field label="GET ID Action Link (Telegram / WhatsApp)" hint="Opens when user clicks GET ID (e.g. https://t.me/... or 919876543210)">
                <TextField value={m.actionLink || m.telegram || ''} onChange={(v) => { upd(i, 'actionLink', v); upd(i, 'telegram', v); }} placeholder="https://t.me/your_telegram" />
              </Field>
              <div className="md:col-span-2">
                <Field label="Visit Site URL" hint="Official website link (e.g. https://example.com)">
                  <TextField value={m.visitUrl || ''} onChange={(v) => upd(i, 'visitUrl', v)} placeholder="https://example.com" />
                </Field>
              </div>
              <div className="md:col-span-3">
                <label className="text-xs font-semibold text-zinc-300 mb-1.5 block">Brand Logo</label>
                <LogoUpload value={m.logo || ''} onChange={(v) => upd(i, 'logo', v)} />
              </div>
            </div>
          </div>
        ))}
        <button onClick={add} className="w-full h-11 rounded-xl border border-dashed border-zinc-700 hover:border-[color:var(--gold)] text-zinc-300 text-sm font-bold flex items-center justify-center gap-2 bg-zinc-900/40 hover:bg-zinc-900 transition-all">
          <Plus size={15} /> Add GET ID Platform
        </button>
      </div>
    </Card>
  );
}
